import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Popper } from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';

export interface NoLocalArvore {
  uid_pesca_local: string;
  uid_pesca_local_pai: string | null;
  nome: string;
  tipo: string;
}

interface FiltroLocalArvoreProps {
  nos: NoLocalArvore[];
  /** Nós marcados. Sempre disjuntos: marcar um pai desmarca os descendentes marcados. */
  selecionados: string[];
  onChange: (uids: string[]) => void;
}

const ROTULO_TIPO: Record<string, string> = {
  bacia: 'bacia', rio: 'rio', represa: 'represa', trecho_municipio: 'trecho', area_pesca: 'área', outro: 'outro',
};

// Acima dos modais da aplicação (1000 a 2000 em uso) e do nível 'modal' do MUI.
const Z_INDEX_DROPDOWN = 2100;

const normalizar = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Dropdown em árvore (bacia > rio/represa > trecho > área) com multisseleção, busca por
 * nome e opção "Todos". A seleção fica sempre disjunta: um nó cujo ancestral já está
 * marcado aparece marcado e travado ("incluído"), e marcar um ancestral limpa os
 * descendentes — assim o filtro nunca conta o mesmo local duas vezes.
 */
const FiltroLocalArvore: React.FC<FiltroLocalArvoreProps> = ({ nos, selecionados, onChange }) => {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const raizRef = useRef<HTMLDivElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      // o painel vive num portal (fora de raizRef): clique nele não conta como "fora"
      const alvo = e.target as Node;
      if (raizRef.current?.contains(alvo) || painelRef.current?.contains(alvo)) return;
      setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const { porUid, filhosDe, raizes } = useMemo(() => {
    const porUid = new Map(nos.map((n) => [n.uid_pesca_local, n]));
    const filhosDe = new Map<string, NoLocalArvore[]>();
    const raizes: NoLocalArvore[] = [];
    for (const n of nos) {
      const pai = n.uid_pesca_local_pai && porUid.has(n.uid_pesca_local_pai) ? n.uid_pesca_local_pai : null;
      if (pai) filhosDe.set(pai, [...(filhosDe.get(pai) || []), n]);
      else raizes.push(n);
    }
    return { porUid, filhosDe, raizes };
  }, [nos]);

  const ancestrais = (uid: string): string[] => {
    const lista: string[] = [];
    let atual = porUid.get(uid);
    while (atual?.uid_pesca_local_pai && porUid.has(atual.uid_pesca_local_pai)) {
      lista.push(atual.uid_pesca_local_pai);
      atual = porUid.get(atual.uid_pesca_local_pai);
    }
    return lista;
  };

  const descendentes = (uid: string): string[] =>
    (filhosDe.get(uid) || []).flatMap((f) => [f.uid_pesca_local, ...descendentes(f.uid_pesca_local)]);

  const marcados = new Set(selecionados);
  const incluidoPorAncestral = (uid: string) => ancestrais(uid).some((a) => marcados.has(a));
  const temDescendenteMarcado = (uid: string) => descendentes(uid).some((d) => marcados.has(d));

  const alternar = (uid: string) => {
    if (marcados.has(uid)) {
      onChange(selecionados.filter((s) => s !== uid));
      return;
    }
    if (incluidoPorAncestral(uid)) return;
    const removidos = new Set(descendentes(uid));
    onChange([...selecionados.filter((s) => !removidos.has(s)), uid]);
  };

  const termo = normalizar(busca);
  // Com busca: mostra os nós que casam + seus ancestrais (pra manter o caminho visível).
  const visiveis = useMemo(() => {
    if (!termo) return null;
    const set = new Set<string>();
    for (const n of nos) {
      if (normalizar(n.nome).includes(termo)) {
        set.add(n.uid_pesca_local);
        ancestrais(n.uid_pesca_local).forEach((a) => set.add(a));
      }
    }
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo, nos]);

  const alternarExpansao = (uid: string) =>
    setExpandidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(uid)) novo.delete(uid); else novo.add(uid);
      return novo;
    });

  const rotuloBotao = selecionados.length === 0
    ? 'Local de pesca: Todos'
    : selecionados.length === 1
      ? `Local: ${porUid.get(selecionados[0])?.nome || '…'}`
      : `Locais: ${selecionados.length} selecionados`;

  const renderNo = (no: NoLocalArvore, nivel: number): React.ReactNode => {
    if (visiveis && !visiveis.has(no.uid_pesca_local)) return null;
    const filhos = filhosDe.get(no.uid_pesca_local) || [];
    const expandido = visiveis ? true : expandidos.has(no.uid_pesca_local);
    const marcado = marcados.has(no.uid_pesca_local);
    const incluido = !marcado && incluidoPorAncestral(no.uid_pesca_local);
    const parcial = !marcado && !incluido && temDescendenteMarcado(no.uid_pesca_local);
    return (
      <React.Fragment key={no.uid_pesca_local}>
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: nivel * 16, minHeight: 30 }}>
          {filhos.length > 0 ? (
            <button
              type="button"
              onClick={() => alternarExpansao(no.uid_pesca_local)}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', color: '#777' }}
              aria-label={expandido ? 'Recolher' : 'Expandir'}
            >
              {expandido ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            </button>
          ) : (
            <span style={{ width: 20, display: 'inline-block' }} />
          )}
          <Checkbox
            size="small"
            checked={marcado || incluido}
            indeterminate={parcial}
            disabled={incluido}
            onChange={() => alternar(no.uid_pesca_local)}
            sx={{ padding: '4px' }}
          />
          <span
            onClick={() => !incluido && alternar(no.uid_pesca_local)}
            style={{ fontSize: '0.85rem', cursor: incluido ? 'default' : 'pointer', color: incluido ? '#999' : '#333' }}
          >
            {no.nome} <small style={{ color: '#aaa' }}>{ROTULO_TIPO[no.tipo] || no.tipo}</small>
          </span>
        </div>
        {expandido && filhos.map((f) => renderNo(f, nivel + 1))}
      </React.Fragment>
    );
  };

  return (
    <div ref={raizRef} style={{ position: 'relative', flex: '0 0 220px' }}>
      <button
        ref={botaoRef}
        type="button"
        onClick={() => setAberto((a) => !a)}
        style={{
          width: '220px', padding: '8px 12px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff',
          textAlign: 'left', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem',
        }}
        title={rotuloBotao}
      >
        {rotuloBotao}
      </button>
      {/* Portal no body (Popper): não é cortado pelo overflow de quadros/cartões que contêm o filtro. */}
      <Popper open={aberto} anchorEl={botaoRef.current} placement="bottom-start" style={{ zIndex: Z_INDEX_DROPDOWN }}>
        <div ref={painelRef} style={{
          width: '340px', background: '#fff', border: '1px solid #ddd',
          borderRadius: '8px', marginTop: '2px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '8px',
        }}>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar local..."
            autoComplete="off"
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', borderRadius: '14px', border: '1px solid #ddd', marginBottom: '6px' }}
          />
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', minHeight: 30 }}>
              <span style={{ width: 20, display: 'inline-block' }} />
              <Checkbox size="small" checked={selecionados.length === 0} onChange={() => onChange([])} sx={{ padding: '4px' }} />
              <span onClick={() => onChange([])} style={{ fontSize: '0.85rem', cursor: 'pointer', fontWeight: 'bold' }}>Todos</span>
            </div>
            {raizes.map((r) => renderNo(r, 0))}
            {visiveis && visiveis.size === 0 && <div style={{ padding: '8px', color: '#999', fontSize: '0.85rem' }}>Nenhum local encontrado.</div>}
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default FiltroLocalArvore;
